// src/components/author/CoAuthoredManuscripts.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  MoreVertical, 
  Eye, 
  Download, 
  Users,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
  RefreshCw,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Tag,
  ArrowLeft,
  ArrowRight,
  Users as UsersIcon,
  Loader
} from 'lucide-react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/toast';
import { useLocation, useNavigate } from 'react-router-dom';
import RichTextViewer from '../common/RichTextViewer';

// Function to parse Quill Delta format to HTML
// Function to parse Quill Delta format to HTML (updated)
const parseQuillContent = (content) => {
  if (!content) return '';

  // If it's already HTML, return as is
  if (typeof content === 'string' && 
      (content.includes('<p>') || content.includes('<div>') || content.includes('<br>'))) {
    return content;
  }

  try {
    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      let html = '';
      content.ops.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.color) text = `<span style="color:${op.attributes.color}">${text}</span>`;
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      return html;
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (parsed.ops) {
          return parseQuillContent(parsed);
        }
        // If parsed has a message property, return that
        if (parsed.message) {
          return parsed.message;
        }
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

const CoAuthoredManuscripts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const urlManuscriptId = queryParams.get('a');
  const urlPage = parseInt(queryParams.get('page')) || 1;
  const urlSearch = queryParams.get('search') || '';
  const urlStatus = queryParams.get('status') || 'all';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manuscripts, setManuscripts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: urlPage,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  
  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(urlSearch);
  const [filter, setFilter] = useState(urlStatus);
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('coauthored_view');
    return saved || 'table';
  });
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  const [selectedManuscriptIndex, setSelectedManuscriptIndex] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('coauthored_view', viewType);
  }, [viewType]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch manuscripts when filters change
  useEffect(() => {
    fetchCoAuthoredManuscripts();
  }, [pagination.currentPage, filter, debouncedSearchTerm]);

  // Check for manuscript ID in URL
  useEffect(() => {
    if (urlManuscriptId && manuscripts.length > 0) {
      const manuscriptIndex = manuscripts.findIndex(m => m.id === urlManuscriptId);
      if (manuscriptIndex !== -1) {
        setSelectedManuscript(manuscripts[manuscriptIndex]);
        setSelectedManuscriptIndex(manuscriptIndex);
        setShowViewModal(true);
      }
    }
  }, [urlManuscriptId, manuscripts]);

  const fetchCoAuthoredManuscripts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/api/authors/coauthored', {
        params: {
          page: pagination.currentPage,
          limit: pagination.limit,
          search: debouncedSearchTerm,
          status: filter,
          sortBy: 'id',
          sortOrder: 'DESC'
        }
      });
      
      if (response.data.status === 'success') {
        setManuscripts(response.data.manuscripts);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load manuscripts');
      }
    } catch (err) {
      console.error('Error fetching co-authored manuscripts:', err);
      showToast('Failed to load manuscripts. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      submitted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      under_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      returned_for_revision: 'bg-blue-100 text-blue-800 border-blue-200',
      returned_for_correction: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'draft':
        return <FileText className="w-3 h-3 mr-1" />;
      case 'submitted':
      case 'under_review':
        return <Clock className="w-3 h-3 mr-1" />;
      case 'returned_for_revision':
      case 'returned_for_correction':
        return <AlertCircle className="w-3 h-3 mr-1" />;
      case 'accepted':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'rejected':
        return <X className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    const texts = {
      draft: 'Draft',
      submitted: 'Submitted',
      under_review: 'In Review',
      returned_for_revision: 'Revision Required',
      returned_for_correction: 'Correction Required',
      accepted: 'Accepted',
      rejected: 'Rejected'
    };
    return texts[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleViewManuscript = (manuscript, index) => {
    setSelectedManuscript(manuscript);
    setSelectedManuscriptIndex(index);
    setShowViewModal(true);
    // Update URL with manuscript ID
    navigate(`/coauthored?a=${manuscript.id}&page=${pagination.currentPage}${searchTerm ? `&search=${searchTerm}` : ''}${filter !== 'all' ? `&status=${filter}` : ''}`, { replace: true });
  };

  const handleNavigateManuscript = (direction) => {
    const newIndex = selectedManuscriptIndex + direction;
    if (newIndex >= 0 && newIndex < manuscripts.length) {
      const manuscript = manuscripts[newIndex];
      setSelectedManuscript(manuscript);
      setSelectedManuscriptIndex(newIndex);
      
      // Update URL with new manuscript ID
      navigate(`/coauthored?a=${manuscript.id}&page=${pagination.currentPage}${searchTerm ? `&search=${searchTerm}` : ''}${filter !== 'all' ? `&status=${filter}` : ''}`, { replace: true });
    }
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedManuscript(null);
    setSelectedManuscriptIndex(null);
    // Remove manuscript ID from URL but keep other params
    navigate(`/coauthored?page=${pagination.currentPage}${searchTerm ? `&search=${searchTerm}` : ''}${filter !== 'all' ? `&status=${filter}` : ''}`, { replace: true });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: page }));
      navigate(`/coauthored?page=${page}${searchTerm ? `&search=${searchTerm}` : ''}${filter !== 'all' ? `&status=${filter}` : ''}`, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate(`/coauthored?page=1${value ? `&search=${value}` : ''}${filter !== 'all' ? `&status=${filter}` : ''}`, { replace: true });
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate(`/coauthored?page=1${searchTerm ? `&search=${searchTerm}` : ''}&status=${value}`, { replace: true });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilter('all');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    navigate('/coauthored', { replace: true });
  };

  const handleRefresh = () => {
    fetchCoAuthoredManuscripts();
  };

  if (loading && manuscripts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading co-authored manuscripts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[--asfi-blue] flex items-center">
              <UsersIcon className="w-6 h-6 mr-3" />
              Manuscripts I Have Co-Authored
            </h1>
            <p className="text-gray-600 mt-2">View all manuscripts where you are listed as a co-author</p>
          </div>

          {/* Action Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Total: <span className="font-medium">{pagination.totalCount}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">
                  Showing: <span className="font-medium">
                    {((pagination.currentPage - 1) * pagination.limit) + 1}-
                    {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-4">
                {/* View Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewType('table')}
                    className={`p-2 rounded-md transition-all ${
                      viewType === 'table' 
                        ? 'bg-white shadow-sm text-[--asfi-blue]' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Table View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewType('grid')}
                    className={`p-2 rounded-md transition-all ${
                      viewType === 'grid' 
                        ? 'bg-white shadow-sm text-[--asfi-blue]' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title, ID, corresponding author, or co-authors..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={filter}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">In Review</option>
                  <option value="returned_for_revision">Revision Required</option>
                  <option value="returned_for_correction">Correction Required</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(searchTerm || filter !== 'all') && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="text-sm text-[--asfi-blue] hover:text-purple-800 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Manuscripts Display */}
          {manuscripts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No co-authored manuscripts found</h3>
              <p className="text-gray-500">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : "You haven't been added as a co-author on any manuscripts yet."}
              </p>
              {(searchTerm || filter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-[--asfi-blue] hover:text-purple-800"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : viewType === 'table' ? (
            /* Table View */
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[--asfi-blue]">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        ID
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Title
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Corresponding Author
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Last Modified
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {manuscripts.map((manuscript, index) => (
                      <tr key={manuscript.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(manuscript.status)}`}>
                            {getStatusIcon(manuscript.status)}
                            {getStatusText(manuscript.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {manuscript.id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {manuscript.title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{manuscript.correspondingAuthor}</span>
                            <span className="text-xs text-gray-400">{manuscript.correspondingEmail}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(manuscript.lastModified)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewManuscript(manuscript, index)}
                              className="p-2 text-gray-600 hover:text-[--asfi-blue] hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {manuscripts.map((manuscript, index) => (
                <div
                  key={manuscript.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[--asfi-blue] mb-1">
                          {manuscript.id}
                        </p>
                        <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-2">
                          {manuscript.title}
                        </h3>
                      </div>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(manuscript.status)}`}>
                        {getStatusIcon(manuscript.status)}
                        {getStatusText(manuscript.status)}
                      </span>
                    </div>

                    {/* Corresponding Author */}
                    <div className="flex items-start mb-3">
                      <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{manuscript.correspondingAuthor}</p>
                        <p className="text-xs text-gray-500 flex items-center mt-0.5">
                          <Mail className="w-3 h-3 mr-1" />
                          {manuscript.correspondingEmail}
                        </p>
                      </div>
                    </div>

                    {/* Co-Authors */}
                    <div className="flex items-start mb-3">
                      <Users className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {manuscript.authors?.filter(a => a !== manuscript.correspondingAuthor).join(', ') || 'No co-authors listed'}
                        </p>
                      </div>
                    </div>

                    {/* Last Modified */}
                    <div className="flex items-center text-xs text-gray-500 mb-4">
                      <Calendar className="w-3 h-3 mr-1" />
                      Last modified: {formatDate(manuscript.lastModified)}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewManuscript(manuscript, index)}
                        className="flex-1 px-3 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded text-sm ${
                          pagination.currentPage === pageNum
                            ? 'bg-[--asfi-blue] text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Manuscript Modal - Enhanced with formatted content */}
      {showViewModal && selectedManuscript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header - Fixed */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Co-Authored Manuscript Details</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedManuscriptIndex + 1} of {manuscripts.length}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Title Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Title</h4>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedManuscript.title}
                  </h2>
                </div>

                {/* Status and Type */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedManuscript.status)}`}>
                    {getStatusIcon(selectedManuscript.status)}
                    {getStatusText(selectedManuscript.status)}
                  </span>
                  <span className="text-sm text-gray-600">
                    Article Type: <span className="font-medium">{selectedManuscript.type || 'Research Article'}</span>
                  </span>
                </div>

                {/* Corresponding Author */}
                <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Corresponding Author</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{selectedManuscript.correspondingAuthor}</p>
                      <p className="text-sm text-gray-600 flex items-center mt-1">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedManuscript.correspondingEmail}
                      </p>
                    </div>
                    <a 
                      href={`mailto:${selectedManuscript.correspondingEmail}`}
                      className="px-3 py-1 bg-[--asfi-blue] text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Contact
                    </a>
                  </div>
                </div>

                {/* All Authors */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">All Authors</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedManuscript.authors?.map((author, index) => {
                      const isCurrentUser = author.includes(user?.firstName || '') && 
                                           author.includes(user?.lastName || '');
                      return (
                        <span
                          key={index}
                          className={`px-3 py-1 text-sm rounded-full ${
                            isCurrentUser
                              ? 'bg-[--asfi-blue] text-white font-medium'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {author}
                          {isCurrentUser && ' (You)'}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Abstract - With Quill parsing */}
                {selectedManuscript.abstract && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-4">Abstract</h4>
                
                    <RichTextViewer content={selectedManuscript.abstract} />
                  </div>
                )}

                {/* Keywords */}
                {selectedManuscript.keywords && selectedManuscript.keywords.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-3">Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedManuscript.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-50 text-[--asfi-blue] text-sm rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Submitted Date</h4>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      {formatDate(selectedManuscript.submittedDate)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Last Modified</h4>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      {formatDate(selectedManuscript.lastModified)}
                    </p>
                  </div>
                </div>

                {/* Files Section */}
                {selectedManuscript.files && Object.keys(selectedManuscript.files).length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-4">Files</h4>
                    <div className="space-y-3">
                      {Object.entries(selectedManuscript.files).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-700 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="ml-2 text-sm text-gray-600">{value}</span>
                          </div>
                          <button className="p-1 text-gray-500 hover:text-[--asfi-blue] hover:bg-white rounded transition-colors">
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Your Contribution */}
                {selectedManuscript.coAuthorRole && (
                  <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-1">Your Contribution</h4>
                    <p className="text-sm text-blue-700">{selectedManuscript.coAuthorRole}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Fixed at bottom */}
            <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Navigation Arrows (Absolute positioned) */}
            <button
              onClick={() => handleNavigateManuscript(-1)}
              disabled={selectedManuscriptIndex === 0}
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${
                selectedManuscriptIndex === 0 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-50 cursor-pointer'
              }`}
              title="Previous Manuscript"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            
            <button
              onClick={() => handleNavigateManuscript(1)}
              disabled={selectedManuscriptIndex === manuscripts.length - 1}
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${
                selectedManuscriptIndex === manuscripts.length - 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 cursor-pointer'
              }`}
              title="Next Manuscript"
            >
              <ArrowRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>
      )}

      {/* Add custom CSS for Quill editor */}
      <style jsx>{`
        .ql-editor {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
        }
        .ql-editor h1 { font-size: 2em; margin: 0.67em 0; }
        .ql-editor h2 { font-size: 1.5em; margin: 0.75em 0; }
        .ql-editor h3 { font-size: 1.17em; margin: 0.83em 0; }
        .ql-editor p { margin: 0 0 1em 0; }
        .ql-editor ul, .ql-editor ol { padding-left: 1.5em; margin: 0.5em 0; }
        .ql-editor li { margin: 0.25em 0; }
        .ql-editor a { color: #7e22ce; text-decoration: underline; }
        .ql-editor strong { font-weight: 600; }
        .ql-editor em { font-style: italic; }
        .ql-editor u { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default CoAuthoredManuscripts;